import type { Dimensions, Position } from '@festina-life/core'

import type { TemplateVersionInfo } from '../../types/templateModel'
import {
  NODE_SPACING_HORIZONTAL,
  NODE_SPACING_VERTICAL,
  TEXT_BOX_PADDING_X,
  TEXT_BOX_PADDING_Y,
} from './constants'
import type {
  Column,
  DisplayNode,
  HierarchicalNode,
  Leaf,
  Node,
  Row,
  Stair,
} from './types'

/**
 * Helper to get the last element in a non-empty array for readability.
 */
const last = <T>(arr: T[]): T => arr[arr.length - 1]

/**
 * Helper to get the position of the right edge of the node.
 */
const right = (node: DisplayNode) => node.offset.x + node.boundingBox.width

/**
 * Helper to get the position of the bottom edge of the node.
 */
const bottom = (node: DisplayNode) => node.offset.y + node.boundingBox.height

/**
 * Return the area of a node.
 */
const area = (node: HierarchicalNode): number =>
  node.size.width * node.size.height

/**
 * Return the total leaf child count of a node.
 */
const childCount = (node: HierarchicalNode): number =>
  node.type === 'leaf'
    ? 1
    : node.children.reduce((acc, child) => acc + childCount(child), 0)

/**
 * Return the first leaf of a node.
 */
const firstLeaf = (node: HierarchicalNode): Leaf =>
  node.type === 'leaf' ? node : firstLeaf(node.children[0])

/**
 * Helper to instantiate a column with the correct dimensions.
 */
const makeCol = (...nodes: HierarchicalNode[]): Column => ({
  type: 'column',
  size: {
    width: Math.max(...nodes.map((node) => node.size.width)),
    height: nodes.reduce((acc, child) => acc + child.size.height, 0),
  },
  children: nodes,
})

/**
 * Helper to instantiate a row with the correct dimensions.
 */
const makeRow = (...nodes: HierarchicalNode[]): Row => ({
  type: 'row',
  size: {
    width: nodes.reduce((acc, child) => acc + child.size.width, 0),
    height: Math.max(...nodes.map((node) => node.size.height)),
  },
  children: nodes,
})

/**
 * Helper to instantiate a stair with the correct dimensions.
 */
const makeStair = (...nodes: HierarchicalNode[]): Stair => ({
  type: 'stair',
  size: {
    width: Math.max(...nodes.map((node) => node.size.width)),
    height: nodes.reduce((acc, node) => acc + node.size.height, 0),
  },
  children: nodes,
})

/**
 * Return the index for the position where the size function returns the smallest value.
 */
const indexOfSmallestArray = <T>(
  arr: T[],
  size: (arr: T) => number,
): number => {
  let smallestIndex = 0
  let smallestSize = size(arr[smallestIndex])

  for (let i = 1; i < arr.length; i += 1) {
    const currentSize = size(arr[i])

    if (currentSize < smallestSize) {
      smallestSize = currentSize
      smallestIndex = i
    }
  }

  return smallestIndex
}

/**
 * Yield all positions of the leaf nodes, accumulating the offsets.
 */
function* leafPositions(
  node: DisplayNode,
  offset: Position,
): Generator<Position> {
  const position = {
    x: offset.x + node.offset.x,
    y: offset.y + node.offset.y,
  }

  if (node.type === 'leaf') {
    yield position
  } else {
    for (const child of node.children) {
      yield* leafPositions(child, position)
    }
  }
}

/**
 * Recursively create a hierarchical layout starting from the root node.
 */
export const createLayout = ({
  templateVersion,
  size,
  parent,
}: {
  templateVersion: TemplateVersionInfo
  size: (templateVersion: TemplateVersionInfo) => Dimensions
  parent?: Leaf
}): HierarchicalNode => {
  const leaf: Leaf = {
    type: 'leaf',
    size: { width: 1, height: 1 },
    templateId: templateVersion.identifier,
    layer: templateVersion.layer,
    block: templateVersion.block,
    templateLabel: templateVersion.identifier,
    boundingBox: size(templateVersion),
    parent,
    replacesId: templateVersion.replacesId,
    isMissing: templateVersion.isMissing,
  }

  const children = templateVersion.children.map((child) =>
    createLayout({ templateVersion: child, size, parent: leaf }),
  )

  if (children.length === 0) {
    return leaf
  }

  if (children.length === 1) {
    return makeCol(leaf, children[0])
  }

  if (
    children.length === 2 &&
    children.every((child) => child.type === 'leaf')
  ) {
    // Order by width (desc), height (desc), child count (desc), name (asc)
    children.sort(
      (childA, childB) =>
        childB.size.width - childA.size.width ||
        childB.size.height - childA.size.height ||
        childCount(childB) - childCount(childA) ||
        firstLeaf(childA).templateLabel.localeCompare(
          firstLeaf(childB).templateLabel,
        ),
    )

    return makeCol(leaf, makeStair(...children))
  }

  // Order by area (desc), height (desc), width (desc), child count (desc), name (asc)
  children.sort(
    (childA, childB) =>
      area(childB) - area(childA) ||
      childB.size.height - childA.size.height ||
      childB.size.width - childA.size.width ||
      childCount(childB) - childCount(childA) ||
      firstLeaf(childA).templateLabel.localeCompare(
        firstLeaf(childB).templateLabel,
      ),
  )

  const buckets: HierarchicalNode[][] =
    // It might be possible to make this check smarter, but this works well.
    children.length > 6 ? [[], [], []] : [[], []]

  for (const child of children) {
    buckets[
      indexOfSmallestArray(buckets, (arr) =>
        arr.reduce((acc, node) => acc + area(node), 0),
      )
    ].push(child)
  }

  return makeCol(
    leaf,
    makeRow(
      ...buckets.map((bucket) =>
        // Avoid unnecessary groupings to make alignment work out.
        bucket.length === 1 ? bucket[0] : makeStair(...bucket),
      ),
    ),
  )
}

/**
 * Position a hierarchical layout with relative offsets.
 */
export const positionLayout = (node: HierarchicalNode): DisplayNode => {
  const offset = { x: 0, y: 0 }

  switch (node.type) {
    case 'column': {
      const children = node.children.map(positionLayout)

      const width = Math.max(
        ...children.map((child) => child.boundingBox.width),
      )

      for (let i = 0; i < children.length; i += 1) {
        const child = children[i]
        child.offset.x = (width - child.boundingBox.width) / 2

        if (i !== 0) {
          child.offset.y = bottom(children[i - 1]) + NODE_SPACING_VERTICAL
        }
      }

      return {
        type: 'group',
        offset,
        boundingBox: {
          width,
          height: bottom(last(children)),
        },
        children,
      }
    }
    case 'row': {
      const children = node.children.map(positionLayout)

      const height = Math.max(
        ...children.map((child) => child.boundingBox.height),
      )

      for (let i = 1; i < children.length; i += 1) {
        const child = children[i]
        const prev = children[i - 1]

        child.offset.x = right(prev) + NODE_SPACING_HORIZONTAL

        // Figure out the positions of nodes below the bottom of the previous node.
        // The X-offset of those leaves can be "ignored" to make better use of the space.
        const edgeY = bottom(prev)
        let minXAbove = Infinity
        let minXBelow = Infinity

        for (const position of leafPositions(child, child.offset)) {
          if (position.y < edgeY) {
            minXBelow = Math.min(minXBelow, position.x)
          } else {
            minXAbove = Math.min(minXAbove, position.x)
          }
        }

        const gap = minXBelow - minXAbove

        if (gap !== Infinity && gap > 0) {
          child.offset.x -= gap
        }
      }

      return {
        type: 'group',
        offset,
        boundingBox: {
          width: right(last(children)),
          height,
        },
        children,
      }
    }
    case 'stair': {
      const children = node.children.map(positionLayout)

      // Each step below in the stair peeks from the side so a link can connect.
      // This offset leaves a gap on the opposite side.
      // The gap is filled by putting larger steps after smaller ones.
      children.sort(
        (childA, childB) => childA.boundingBox.width - childB.boundingBox.width,
      )

      for (let i = 1; i < children.length; i += 1) {
        const prev = children[i - 1]
        const child = children[i]
        child.offset.x =
          right(prev) + NODE_SPACING_HORIZONTAL - child.boundingBox.width
        child.offset.y = bottom(prev) + NODE_SPACING_VERTICAL / 2

        if (child.type === 'group') {
          if (child.children[0].type !== 'leaf') {
            throw new Error('First child of a group must be a centered leaf')
          }
          // This "knows" a stair's group is a centered column.
          // Might be able to clean this up.
          child.children[0].connection = 'right'
          child.children[0].offset.x +=
            (child.boundingBox.width - child.children[0].boundingBox.width) / 2
        } else {
          child.connection = 'right'
        }
      }

      // If the largest step is not deep enough, it might have gone into the negatives.
      const minX = Math.min(...children.map((child) => child.offset.x))

      for (const child of children) {
        child.offset.x -= minX
      }

      return {
        type: 'group',
        offset,
        boundingBox: {
          width: Math.max(...children.map((child) => right(child))),
          height: bottom(last(children)),
        },
        children,
      }
    }
    case 'leaf': {
      return {
        type: 'leaf',
        offset,
        boundingBox: {
          width: node.boundingBox.width + TEXT_BOX_PADDING_X * 2,
          height: node.boundingBox.height + TEXT_BOX_PADDING_Y * 2,
        },
        text: node.templateLabel,
        id: node.templateId,
        layer: node.layer,
        block: node.block,
        parentText: node.parent?.templateLabel,
        connection: 'center',
        replacesId: node.replacesId,
        isMissing: node.isMissing,
      }
    }
  }
}

const resolveLayoutPosition = ({
  node,
  position: { x, y },
  createdNodes,
}: {
  node: DisplayNode
  position: Position
  createdNodes: Record<string, Node>
}) => {
  const position = {
    x: x + node.offset.x,
    y: y + node.offset.y,
  }

  if (node.type === 'group') {
    for (const child of node.children) {
      resolveLayoutPosition({
        node: child,
        position,
        createdNodes,
      })
    }
  } else {
    createdNodes[node.text] = {
      position,
      boundingBox: node.boundingBox,
      text: node.text,
      id: node.id,
      layer: node.layer,
      block: node.block,
      connection: node.connection,
      children: [],
      replacesId: node.replacesId,
      isMissing: node.isMissing,
    }
  }
}

const resolveLayoutChildren = ({
  node,
  createdNodes,
}: {
  node: DisplayNode
  createdNodes: Record<string, Node>
}) => {
  if (node.type === 'group') {
    for (const child of node.children) {
      resolveLayoutChildren({
        node: child,
        createdNodes,
      })
    }
  } else if (node.parentText) {
    createdNodes[node.parentText].children.push(createdNodes[node.text])
  }
}

/**
 * Resolve a nested hierarchical layout with relative offsets
 * to use absolute positions and object references for links.
 *
 * Nodes are sorted from top-to-bottom, then left-to-right.
 * */
export const resolveLayout = (root: DisplayNode): Node[] => {
  const createdNodes: Record<string, Node> = {}
  resolveLayoutPosition({ node: root, position: { x: 0, y: 0 }, createdNodes })
  resolveLayoutChildren({ node: root, createdNodes })

  return Object.values(createdNodes).sort((nodeA, nodeB) =>
    nodeA.position.y === nodeB.position.y
      ? nodeA.position.x - nodeB.position.x
      : nodeA.position.y - nodeB.position.y,
  )
}
